import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useParams } from "react-router";
import { canViewAllHubTasks } from "@bafe/types";

import { activeMembership, useSession } from "../../lib/session";
import { useOrganisationStore } from "../../lib/stores";

import { Popup } from "../../components/Popup";
import { HubPage } from "./HubPages.js";
import { hubWorkOrdersPath } from "./hub-work-orders.js";
import { SALES_ORDER_LABEL } from "./hub-sales-order-label.js";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";
import { workspaceHref } from "../../lib/workspace-url.js";

function useHubRecords() {
	return useQuery({
		queryKey: queryKeys.hubRecords,
		queryFn: () => api.getHubRecords(),
	});
}

function useHostedSites() {
	return useQuery({
		queryKey: queryKeys.hostedSites,
		queryFn: () => api.listHostedSites(),
	});
}

function Gate({
	query,
	children,
}: {
	query: { isLoading: boolean; isError: boolean; error: unknown };
	children: ReactNode;
}) {
	if (query.isLoading) return <p className="empty">Loading records.</p>;
	if (query.isError) return <p className="err">{presentError(query.error).title}</p>;
	return children;
}

function CountTable({ rows }: { rows: Array<[string, number]> }) {
	return (
		<div className="table-wrap">
			<table>
				<thead>
					<tr>
						<th>Area</th>
						<th>Records</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(([label, count]) => (
						<tr key={label}>
							<td>{label}</td>
							<td className="quiet">{count}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export const MY_HUB_PAGES: Array<{ path: string; title: string }> = [
	{ path: "my/tasks", title: "My Tasks" },
	{ path: "my/profile", title: "My profile" },
	{ path: "my/claims", title: "My claims" },
	{ path: "my/payslip", title: "My Payslip" },
	{ path: "my/ea-form", title: "My EA Form" },
	{ path: "my/leave", title: "My Leave" },
	{ path: "my/attendance", title: "My Attendance" },
];

export function HubMine({ title }: { title: string }) {
	return (
		<div className="card">
			<h2>{title}</h2>
			<p className="sub">Nothing here yet.</p>
		</div>
	);
}

function useHubMembership() {
	const session = useSession();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	return session.data ? activeMembership(session.data, organisationId) : null;
}

export function HubHome() {
	const membership = useHubMembership();
	if (!membership) return <HubOverview />;
	if (membership.role === "user") {
		return <Navigate to={membership.viewAllHubTasks ? "/hub/tasks" : "/hub/my/tasks"} replace />;
	}
	return <HubOverview />;
}

export function HubTeamTasksGate({ children }: { children: ReactNode }) {
	const membership = useHubMembership();
	const { workspaceSlug } = useParams();
	if (!membership) return children;
	const viewAll = canViewAllHubTasks(membership.role, membership.viewAllHubTasks === true);
	if (!viewAll) {
		const target = workspaceSlug
			? workspaceHref(workspaceSlug, "hub/my/tasks")
			: "/hub/my/tasks";
		return <Navigate to={target} replace />;
	}
	return children;
}

export function HubStaffPageGate({ children }: { children: ReactNode }) {
	const membership = useHubMembership();
	if (membership?.role === "user") return <Navigate to="/hub/my/tasks" replace />;
	return children;
}

export function HubOverview() {
	const query = useHubRecords();
	return (
		<Gate query={query}>
			{query.data && (
				<div>
					<p className="sub">What this workspace has in Hub.</p>
					<CountTable
						rows={[
							["Brands", query.data.brands.length],
							["Tasks", query.data.tasks.length],
							["Leads", query.data.leads.length],
							["Expenses", query.data.expenses.length],
							["People", query.data.people.length],
							["Activity", query.data.activity.length],
						]}
					/>
				</div>
			)}
		</Gate>
	);
}

export const HUB_PAGES: Array<{ path: string; title: string }> = [
	{ path: "tasks", title: "Task" },
	{ path: "calendar", title: "Calendar" },
	{ path: "organisation", title: "Organisation" },
	{ path: "inventory", title: "Inventory" },
	{ path: "consumables", title: "Consumables" },
	{ path: "customer-sites", title: "Site" },
	{ path: "projects", title: "Project" },
	{ path: "work-orders", title: "Work Order" },
	{ path: "leads", title: "Leads" },
	{ path: "deals", title: "Deals" },
	{ path: "items", title: "Item" },
	{ path: "quotations", title: "Quotation" },
	{ path: "sales-orders", title: SALES_ORDER_LABEL },
	{ path: "invoices", title: "Invoice" },
	{ path: "receipts", title: "Receipts" },
	{ path: "expenses", title: "Expenses" },
	{ path: "bills", title: "Bills" },
	{ path: "purchase-orders", title: "Purchase order" },
	{ path: "credits", title: "Credits" },
	{ path: "leave", title: "Leave" },
	{ path: "attendance", title: "Attendance" },
	{ path: "claims", title: "Claims" },
	{ path: "payroll", title: "Payroll" },
	{ path: "people-settings", title: "People Settings" },
	{ path: "internal", title: "Internal" },
	{ path: "transactions", title: "Transactions" },
	{ path: "journals", title: "Journals" },
	{ path: "accounts", title: "Charts of accounts" },
	{ path: "tax", title: "Tax" },
	{ path: "audit", title: "Audit" },
	{ path: "assets", title: "Asset" },
	{ path: "maintenance", title: "Maintenance" },
	{ path: "letters", title: "Letters" },
	{ path: "repairs", title: "Repairs" },
	{ path: "register", title: "Register" },
	{ path: "credentials", title: "Credentials" },
	{ path: "documents", title: "Documents" },
	{ path: "vehicles", title: "Vehicle" },
	{ path: "phones", title: "Phone number" },
	{ path: "reports", title: "Reports" },
	{ path: "activity", title: "Activity logs" },
	{ path: "integration", title: "Integration" },
];

export function HubSection({ id }: { id: string }) {
	return <HubPage id={id} />;
}

/** Preserves query string when moving from /hub/work to /hub/work-orders. */
export function HubLegacyWorkRedirect() {
	const location = useLocation();
	return <Navigate to={`${hubWorkOrdersPath()}${location.search}`} replace />;
}

export function SiteOverview() {
	const query = useHostedSites();
	return (
		<Gate query={query}>
			{query.data && (
				<div>
					<p className="sub">Websites in this workspace. Open Sites, Databases, or Redirects for the rest.</p>
					<CountTable rows={[["Sites", query.data.sites.length]]} />
				</div>
			)}
		</Gate>
	);
}

function closeMenu(target: EventTarget) {
	if (target instanceof Element) target.closest("details")?.removeAttribute("open");
}

function Toggle({
	checked,
	disabled,
	label,
	onChange,
}: {
	checked: boolean;
	disabled?: boolean;
	label: string;
	onChange: () => void;
}) {
	return (
		<button
			type="button"
			className="toggle"
			role="switch"
			aria-checked={checked}
			aria-label={label}
			disabled={disabled}
			onClick={onChange}
		/>
	);
}

function SiteRow({
	id,
	hostname,
	enabled,
	wwwMode,
	tags,
	catalog,
}: {
	id: string;
	hostname: string;
	enabled: boolean;
	wwwMode: "none" | "to_apex" | "to_www";
	tags: Array<{ id: string; name: string }>;
	catalog: Array<{ id: string; name: string }>;
}) {
	const client = useQueryClient();
	const [error, setError] = useState<string | null>(null);
	const refresh = async () => {
		await client.invalidateQueries({ queryKey: queryKeys.hostedSites });
	};
	const toggle = useMutation({
		mutationFn: (next: boolean) => api.updateHostedSite(id, { enabled: next }),
		onSuccess: async () => {
			setError(null);
			await refresh();
		},
		onError: (caught) => setError(presentError(caught).title),
	});
	const www = useMutation({
		mutationFn: (next: "none" | "to_apex") => api.updateHostedSite(id, { wwwMode: next }),
		onSuccess: async () => {
			setError(null);
			await refresh();
		},
		onError: (caught) => setError(presentError(caught).title),
	});
	const wwwOn = wwwMode === "to_apex";
	const assign = useMutation({
		mutationFn: (tagIds: string[]) => api.setHostedSiteTags(id, tagIds),
		onSuccess: async () => {
			setError(null);
			await refresh();
		},
		onError: (caught) => setError(presentError(caught).title),
	});
	const chosen = new Set(tags.map((tag) => tag.id));
	const available = catalog.filter((tag) => !chosen.has(tag.id));
	return (
		<tr>
			<td>
				{hostname}
				{error && <p className="err">{error}</p>}
			</td>
			<td>
				<span className="tags">
					{tags.map((tag) => (
						<button
							key={tag.id}
							className="tag"
							type="button"
							disabled={assign.isPending}
							aria-label={`Remove ${tag.name} from ${hostname}`}
							onClick={() => assign.mutate(tags.filter((item) => item.id !== tag.id).map((item) => item.id))}
						>
							{tag.name}
							<span aria-hidden="true">×</span>
						</button>
					))}
					{available.length > 0 && (
						<details className="menu tag-add">
							<summary aria-label={`Add a tag to ${hostname}`}>+</summary>
							<div className="select-pop">
								{available.map((tag) => (
									<button
										key={tag.id}
										className="select-item"
										type="button"
										onClick={(event) => {
											closeMenu(event.target);
											assign.mutate([...tags.map((item) => item.id), tag.id]);
										}}
									>
										{tag.name}
									</button>
								))}
							</div>
						</details>
					)}
				</span>
			</td>
			<td>
				<Toggle
					checked={wwwOn}
					disabled={www.isPending}
					label={`Send www to the root name for ${hostname}`}
					onChange={() => www.mutate(wwwOn ? "none" : "to_apex")}
				/>
			</td>
			<td className="row-end">
				<label className="check">
					<input
						type="checkbox"
						checked={enabled}
						disabled={toggle.isPending}
						onChange={() => toggle.mutate(!enabled)}
						aria-label={`${enabled ? "Disable" : "Enable"} ${hostname}`}
					/>
					<span>{enabled ? "On" : "Off"}</span>
				</label>
				<Link className="btn" to={`/site/sites/${id}/files`}>
					Files
				</Link>
			</td>
		</tr>
	);
}

export function SiteList() {
	const client = useQueryClient();
	const sites = useQuery({ queryKey: queryKeys.hostedSites, queryFn: () => api.listHostedSites() });
	const tags = useQuery({ queryKey: queryKeys.siteTags, queryFn: () => api.listSiteTags() });
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const catalog = tags.data?.tags ?? [];
	const addTag = useMutation({
		mutationFn: () => api.createSiteTag(name),
		onSuccess: async () => {
			setName("");
			setError(null);
			await client.invalidateQueries({ queryKey: queryKeys.siteTags });
		},
		onError: (caught) => setError(presentError(caught).title),
	});
	const removeTag = useMutation({
		mutationFn: (id: string) => api.deleteSiteTag(id),
		onSuccess: async () => {
			setError(null);
			await client.invalidateQueries({ queryKey: queryKeys.siteTags });
			await client.invalidateQueries({ queryKey: queryKeys.hostedSites });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	function onAdd(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (name.trim() === "") {
			setError("Enter a tag, such as main or leadgen.");
			return;
		}
		addTag.mutate();
	}

	return (
		<Gate query={sites}>
			{sites.data && sites.data.sites.length === 0 && (
				<div className="card">
					<h2>No websites yet</h2>
					<p className="sub">Turn on a website for a domain in Manage. Its files will show up here.</p>
				</div>
			)}
			{sites.data && sites.data.sites.length > 0 && (
				<div>
					<div className="row head-actions">
						<p className="sub">
							Open a domain to see its files, upload, or edit a text file. Off shows a page that says n/a.
						</p>
						<button
							className="btn"
							type="button"
							onClick={() => {
								setError(null);
								setOpen(true);
							}}
						>
							Tags
						</button>
					</div>
					<div className="table-wrap">
						<table>
							<caption className="sr-only">Website domains</caption>
							<thead>
								<tr>
									<th>Domain</th>
									<th>Tags</th>
									<th>www to root</th>
									<th className="row-end">Site</th>
								</tr>
							</thead>
							<tbody>
								{sites.data.sites.map((site) => (
									<SiteRow
										key={site.id}
										id={site.id}
										hostname={site.hostname}
										enabled={site.status !== "paused"}
										wwwMode={site.wwwMode}
										tags={site.tags ?? []}
										catalog={catalog}
									/>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
			<Popup
				open={open}
				title="Tags"
				onClose={() => {
					if (addTag.isPending || removeTag.isPending) return;
					setOpen(false);
					setError(null);
				}}
			>
				{error && <p className="err">{error}</p>}
				<p className="sub">
					Add a tag such as main or leadgen, then choose it on a domain. Deleting a tag removes it from every
					domain.
				</p>
				<form onSubmit={onAdd}>
					<label htmlFor="tag-name">New tag</label>
					<input
						id="tag-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder="leadgen"
						autoComplete="off"
						autoFocus
					/>
					<p className="actions">
						<button className="btn" type="submit" disabled={addTag.isPending}>
							{addTag.isPending ? "Adding" : "Add tag"}
						</button>
						<button
							className="btn ghost"
							type="button"
							onClick={() => {
								setOpen(false);
								setError(null);
							}}
						>
							Close
						</button>
					</p>
				</form>
				{catalog.length === 0 ? (
					<p className="sub">No tags yet.</p>
				) : (
					<ul className="tag-list">
						{catalog.map((tag) => (
							<li key={tag.id}>
								<span>{tag.name}</span>
								<button
									className="btn ghost"
									type="button"
									disabled={removeTag.isPending}
									onClick={() => removeTag.mutate(tag.id)}
								>
									Delete
								</button>
							</li>
						))}
					</ul>
				)}
			</Popup>
		</Gate>
	);
}

export { SiteDatabases } from "./SiteDatabases";
export { SiteRedirects } from "./SiteRedirects";
